import {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItemIcon from '@mui/material/ListItemIcon'
import MultilineChart from '@mui/icons-material/MultilineChart'
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined'
import {CssBaseline} from '@mui/material'
import FilterPanelComponent from '../FilterPanel/FilterPanelComponent'
import {
  classes,
  Root,
  StyledList,
  StyledTooltip,
  StyledDashboardListItemButton,
  StyledUserAppListButton
} from './SideBarStyle'
import UserInfoComponent from '../UserInfo/UserInfoComponent'
import AppInfoComponent from '../AppInfo/AppInfoComponent'
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined'
import {useFilters} from '../../store/hooks/filters'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import Info from '@mui/icons-material/Info'
import buttonTitles from '../../appConfig/buttonTitles.config.json'
import ListItemText from '@mui/material/ListItemText'
import {useLocation, useNavigate} from 'react-router-dom'
import InsightsSharpIcon from '@mui/icons-material/InsightsSharp'
import {usePermission} from '../../store/hooks/permission'
import {useTableViewContext} from '../../store/hooks/tableView'
import PersonaSidebar from '../PersonaSidebar/PersonaSidebar'
import {usePercentagePopup} from '../../store/hooks/percentagePopup'
import {useAdminPermission} from '../../store/hooks/adminPermission'
import {useUserFeedback} from '../../store/hooks/userFeedback'

const SideBarComponent = ({
  isAdminPanelOpen,
  setIsAdminPanelOpen,
  deliveryAdmin,
  superAdmin,
  customerSupport,
  viewer,
  isTimeSeriesOpen,
  setIsTimeSeriesOpen,
  isFAQopen,
  setIsFAQopen
}) => {
  const {refreshDashboard} = useAdminPermission()
  const {filtersOpen, setFiltersOpen} = useFilters()
  const navigate = useNavigate()

  const {permission} = usePermission()
  // For Sonar Code Smell this has been change from trendAnalysisFlagValue?.[0] == 1 ? true : false
  const isTrendAnalysisAllowed =
    permission?.healthEquityConditionList?.trendAnalysisPermissions?.[0] === 1 || deliveryAdmin

  const {isRecording} = useUserFeedback()

  const filterPanelRef = useRef(null)
  const openFilterPanelRef = useRef(null)

  const location = useLocation()
  const [selectedIndex, setSelectedIndex] = useState(0)
  // State to control FAQ tooltip visibility on app load
  const {popupOpen} = usePercentagePopup()

  const {
    updateTogglePopulationDensity,
    updateTogglePopulationDistribution,
    updateTogglePopulationOverTime,
    updateToggleRiskOverTime
  } = useTableViewContext()
  const [anchorUserInfoEl, setAnchorUserInfoEl] = useState(null)

  const openUserInfo = Boolean(anchorUserInfoEl)
  const handleUserInfoClick = (event) => {
    setAnchorUserInfoEl(event.currentTarget)
  }

  const [anchorAppInfoEl, setAnchorAppInfoEl] = useState(null)
  const openAppInfo = Boolean(anchorAppInfoEl)
  const handleAppInfoClick = (event) => {
    setAnchorAppInfoEl(event.currentTarget)
  }

  useEffect(() => {
    filtersOpen && filterPanelRef.current?.focus()
  }, [filtersOpen])

  const filterPanelReference = useRef(null)
  const handleFilterPanelClick = (event, key) => {
    event.preventDefault()
    if (key === 'shiftKey') {
      openFilterPanelRef.current?.focus()
    }
  }

  useEffect(() => {
    if (location.pathname === '/') setSelectedIndex(1)
    if (location.pathname.startsWith('/timeseries')) setSelectedIndex(2)
    if (location.pathname.startsWith('/adminpanel')) setSelectedIndex(3)
    if (location.pathname.startsWith('/faq')) setSelectedIndex(4)
  }, [location.pathname])
  useEffect(() => {
    isAdminPanelOpen && setFiltersOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminPanelOpen])

  const handleDashboardNavigation = () => {
    if (refreshDashboard) {
      globalThis.location.href = '/'
    } else {
      navigate('/')
    }
  }

  return (
    <Root addTopmargin={isAdminPanelOpen || isTimeSeriesOpen}>
      <CssBaseline />
      <Drawer
        open
        variant='permanent'
        className={classNames(classes.drawer)}
        classes={{
          paper: classNames(classes.drawerPaper)
        }}
        transitionDuration={300}
      >
        <aside aria-label='Sidebar containing Filters, Point in time, Timeseries and Administration tabs'>
          <List>
            <nav>
              <StyledTooltip title={buttonTitles.sidebarDashboard} placement='right'>
                <StyledDashboardListItemButton
                  isAdminPanelOpen={isAdminPanelOpen}
                  id={buttonTitles.sidebarDashboard}
                  selected={selectedIndex === 1}
                  aria-label={buttonTitles.sidebarDashboard}
                  ref={openFilterPanelRef}
                  onClick={() => {
                    setIsAdminPanelOpen(false)
                    setIsTimeSeriesOpen(false)
                    setSelectedIndex(1)
                    updateTogglePopulationDensity(false)
                    updateTogglePopulationDistribution(false)
                    setIsFAQopen(false)
                    handleDashboardNavigation()
                  }}
                >
                  <ListItemIcon>
                    <MultilineChart
                      classes={{
                        colorPrimary: classes.icon,
                        root: selectedIndex === 1 ? classes.toggledIcon : classes.untoggledIcon
                      }}
                      color='primary'
                    />
                  </ListItemIcon>
                  <ListItemText primary={buttonTitles.sidebarDashboard} />
                </StyledDashboardListItemButton>
              </StyledTooltip>
            </nav>
            <nav>
              {isTrendAnalysisAllowed && (
                <StyledTooltip title={buttonTitles.sidebarTrendAnalysis} placement='right'>
                  <StyledDashboardListItemButton
                    isTimeSeriesOpen={isTimeSeriesOpen}
                    selected={selectedIndex === 2}
                    onClick={() => {
                      setFiltersOpen(false)
                      setIsAdminPanelOpen(false)
                      setIsTimeSeriesOpen(true)
                      setIsFAQopen(false)
                      setSelectedIndex(2)
                      updateTogglePopulationOverTime(false)
                      updateToggleRiskOverTime(false)
                      navigate('timeseries')
                    }}
                    id={buttonTitles.sidebarTrendAnalysis}
                    aria-label={buttonTitles.sidebarTrendAnalysis}
                  >
                    <ListItemIcon>
                      <InsightsSharpIcon
                        classes={{
                          colorPrimary: classes.icon,
                          root: selectedIndex === 2 ? classes.toggledIcon : classes.untoggledIcon
                        }}
                        color='primary'
                      />
                    </ListItemIcon>
                    <ListItemText primary={buttonTitles.sidebarTrendAnalysis} />
                  </StyledDashboardListItemButton>
                </StyledTooltip>
              )}
            </nav>
            <nav>
              {(deliveryAdmin || superAdmin || customerSupport) && (
                <StyledTooltip title={buttonTitles.sidebarAdminPanel} placement='right'>
                  <StyledDashboardListItemButton
                    isAdminPanelOpen={isAdminPanelOpen}
                    disabled={isRecording}
                    id={buttonTitles.sidebarAdminPanel}
                    aria-label={buttonTitles.sidebarAdminPanel}
                    selected={selectedIndex === 3}
                    onClick={() => {
                      setFiltersOpen(false)
                      setIsTimeSeriesOpen(false)
                      setIsAdminPanelOpen(true)
                      setIsFAQopen(false)
                      setSelectedIndex(3)
                      navigate('adminpanel')
                    }}
                  >
                    <ListItemIcon>
                      <FolderSharedOutlinedIcon
                        classes={{
                          colorPrimary: classes.icon,
                          root: selectedIndex === 3 ? classes.toggledIcon : classes.untoggledIcon
                        }}
                        color='primary'
                      />
                    </ListItemIcon>
                    <ListItemText primary={buttonTitles.sidebarAdminPanel} />
                  </StyledDashboardListItemButton>
                </StyledTooltip>
              )}
              <StyledTooltip title={buttonTitles.sidebarFAQ} placement='right' open={popupOpen}>
                {(viewer || deliveryAdmin || superAdmin || customerSupport) && (
                  <StyledDashboardListItemButton
                    id={buttonTitles.sidebarFAQ}
                    aria-label={buttonTitles.sidebarFAQ}
                    selected={selectedIndex === 4}
                    isFAQopen={isFAQopen}
                    onClick={() => {
                      setFiltersOpen(false)
                      setIsAdminPanelOpen(false)
                      setIsTimeSeriesOpen(false)
                      setIsFAQopen(true)
                      setSelectedIndex(4)
                      navigate('faq')
                    }}
                  >
                    <ListItemIcon>
                      <QuizOutlinedIcon
                        classes={{
                          colorPrimary: classes.icon,
                          root: selectedIndex === 4 ? classes.toggledIcon : classes.untoggledIcon
                        }}
                        color='primary'
                      />
                    </ListItemIcon>
                    <ListItemText primary={buttonTitles.sidebarFAQ} />
                  </StyledDashboardListItemButton>
                )}
              </StyledTooltip>
            </nav>
          </List>
        </aside>
        <aside aria-label='Sidebar containing profile and information tabs'>
          <StyledList>
            <nav>
              <StyledTooltip title={buttonTitles.sidebarUserInfo} placement='right'>
                <StyledUserAppListButton
                  openUserInfo={openUserInfo}
                  onClick={handleUserInfoClick}
                  id={buttonTitles.sidebarUserInfo}
                  aria-label={buttonTitles.sidebarUserInfo}
                >
                  <ListItemIcon>
                    <AccountCircleIcon
                      data-testid='name'
                      classes={{
                        colorPrimary: classes.icon,
                        root: openUserInfo ? classes.toggledIcon : classes.untoggledIcon
                      }}
                      className={classes.iconStyle}
                    />
                  </ListItemIcon>
                  <ListItemText primary={buttonTitles.sidebarUserInfo} />
                </StyledUserAppListButton>
              </StyledTooltip>
            </nav>
            <nav>
              <StyledTooltip title={buttonTitles.sidebarAppInfo} placement='right'>
                <StyledUserAppListButton
                  openAppInfo={openAppInfo}
                  onClick={handleAppInfoClick}
                  id={buttonTitles.sidebarAppInfo}
                  aria-label={buttonTitles.sidebarAppInfo}
                >
                  <ListItemIcon>
                    <Info
                      data-testid='info'
                      classes={{
                        root: openAppInfo ? classes.toggledIcon : classes.untoggledIcon
                      }}
                      className={classes.iconStyle2}
                    />
                  </ListItemIcon>
                  <ListItemText primary={buttonTitles.sidebarAppInfo} />
                </StyledUserAppListButton>
              </StyledTooltip>
            </nav>
          </StyledList>
        </aside>
      </Drawer>
      <div className='component-container' ref={filterPanelRef} tabIndex='-1'>
        <FilterPanelComponent
          classesName={classes.filters}
          ref={filterPanelReference}
          handleFilterPanelClick={handleFilterPanelClick}
        />
      </div>
      <div className='component-container' ref={filterPanelRef} tabIndex='-1'>
        <PersonaSidebar classesName={classes.filters} />
      </div>
      {openUserInfo && (
        <UserInfoComponent
          openUserInfo={openUserInfo}
          anchorUserInfoEl={anchorUserInfoEl}
          setAnchorUserInfoEl={setAnchorUserInfoEl}
        />
      )}
      {openAppInfo && (
        <AppInfoComponent
          openAppInfo={openAppInfo}
          anchorAppInfoEl={anchorAppInfoEl}
          setAnchorAppInfoEl={setAnchorAppInfoEl}
        />
      )}
    </Root>
  )
}

SideBarComponent.propTypes = {
  isAdminPanelOpen: PropTypes.bool,
  setIsAdminPanelOpen: PropTypes.func,
  deliveryAdmin: PropTypes.bool,
  superAdmin: PropTypes.bool,
  customerSupport: PropTypes.bool,
  viewer: PropTypes.bool,
  isTimeSeriesOpen: PropTypes.bool,
  setIsTimeSeriesOpen: PropTypes.func,
  isFAQopen: PropTypes.bool,
  setIsFAQopen: PropTypes.func
}

export default SideBarComponent

/*
 
Component Understanding: SideBarComponent manages the sidebar navigation, including dashboard, trend analysis, 
admin panel, FAQ, user info, and app info, with dynamic state handling and navigation.

Function Descriptions:
  - handleUserInfoClick: Toggles the user info popover visibility.
  - handleAppInfoClick: Toggles the app info popover visibility.
  - handleFilterPanelClick: Handles focus for the filter panel on specific key press.

 */
